import { useLocalization, useSelectedOrdersRows } from 'src/Hooks';
import { useSelector } from 'react-redux';
import { globalSelectors } from 'src/Redux/Global/selectors';
import { useMemo } from 'react';
import { localizationUtils } from 'src/Utils/localizationUtils';
import { FieldState } from 'src/Types';
import { orderTableRowUtils } from 'src/Types/OrderTableRowUtils';
import { accountSettingsSelectors } from '@/Pages/Settings/modules/MarketplacesSettings/services/selectors';

export const useInvoiceTooltipMessage = (): FieldState => {
  const {
    models: {
      OrderTableRow: { InvoiceNumber, parts, CommonErrors },
    },
  } = useLocalization();

  const { orderRows, isSalesPage, isPurchasesPage } = useSelectedOrdersRows();
  const localization = useSelector(globalSelectors.localization);
  const mpAccounts = useSelector(accountSettingsSelectors.mpAccounts);

  return useMemo(() => {
    let disabledDocumentMessage: string | undefined;

    // Check marketplace credential start
    if (isSalesPage) {
      const mpAccountIds = orderRows.map((item) => item.mpAccountId);
      const invoiceCreateDisabled = mpAccountIds.some((id) => {
        const orderMpAccount = mpAccounts.find((account) => account.id === id);
        return !orderMpAccount?.allowCreateInvoices;
      });

      if (invoiceCreateDisabled) {
        return {
          value: '',
          isDocumentIconDisabled: invoiceCreateDisabled,
          disabledDocumentMessage: InvoiceNumber.InvoiceCreateDisabledForMpAccountMessage,
        };
      }
    }
    // Check marketplace credential end

    const result = orderRows.reduce(
      (res, current) => {
        if (current.currentInvoiceCreatedTime) {
          res.withOrderNumbers.add(current.currentInvoiceNumber ?? '');
        }
        if (orderTableRowUtils.isOrderIsAmazonPending(current)) {
          res.amazonInProcessOrders.add(current.userOrderId.toString());
        }

        return res;
      },
      {
        withOrderNumbers: new Set<string>(),
        amazonInProcessOrders: new Set<string>(),
      },
    );

    const withOrderNumbers = [...result.withOrderNumbers];
    const amazonInProcessOrders = [...result.amazonInProcessOrders];
    const isIconDisabled = Boolean(withOrderNumbers.length || amazonInProcessOrders.length);

    if (withOrderNumbers.length) {
      disabledDocumentMessage = localizationUtils.insertTranslatedParamsToString(
        InvoiceNumber.DisableHeaderDocumentMessage,
        [withOrderNumbers.join(', '), withOrderNumbers.length],
        parts,
        localization,
      );
    } else if (amazonInProcessOrders.length) {
      disabledDocumentMessage = CommonErrors.AmazonOrderPending.DisableHeaderDocumentMessage;
    } // if

    return {
      value: '',
      isDocumentIconDisabled: isIconDisabled || isPurchasesPage,
      disabledDocumentMessage,
    };
  }, [localization, orderRows, isSalesPage, isPurchasesPage, mpAccounts]);
};
